import React from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { App } from '../../../app';
import { forMinWidth } from '../../../theme/breakpoints';
import { reactivationRules } from '../Details/reactivationRules';
import { Icon } from '@fluentui/react/lib/Icon';
import { ChildPost, PostType, RootPost } from '../../../RootPost';
import HiddenTextInfo from '../Details/HiddenTextInfo';
import HtmlPreview from '../../shared/HtmlPreview';

export interface CommentsProps {
    ticket: RootPost;
}

export default function Comments({ ticket }: CommentsProps): JSX.Element {
    const comments: ChildPost[] = ticket.children.filter(child => child.type === PostType.comment);
    return <Container>
        {ticket && <CommentsList ticket={ticket} comments={comments} />}
    </Container>
}

export const CommentsList = ({ ticket, comments }: { ticket: RootPost; comments: ChildPost[] }): JSX.Element => {
    const getCommentByText = (name: string, date: any): string => {
        if (date == null) { // if no date returned from server
            return ResourceStrings.errorUnknown();
        }
        return ResourceStrings.commentBy(name, App.ParseDateTimeToLocal(date));
    }

    const reactivationRule = reactivationRules[ticket?.state];

    return <>
        {comments.map((item, idx) => <CommentWrapper key={idx} className="comment">
            {/* <img src={App.rewriteImageUrlToVsfProd(item.profileImageLink)} alt='profile' /> */}
            <div aria-label={getCommentByText(item.author.name, item.createdDate)}>
                {getCommentByText(item.author.name, item.createdDate)}
            </div>

            {!item.isPublic && ticket.isPublic && (
                <div className="private-indicator" title={`Visible to: ${(item.recipientNames ?? "")}`}>
                    <Icon iconName="LockSolid" />
                    <span>private</span>
                </div>
            )}

            {item.reaction === "reactYes" && <div>{reactivationRule?.yesTitle}</div>}
            <HiddenTextInfo post={item} currentUserId={ticket.currentUserId} />

            {item.reaction !== "reactYes" && <HtmlPreview text={item.text} isPublic={item.isPublic} className="details" />}

            {item.children && item.children.length !== 0 && <CommentsList ticket={ticket} comments={item.children} />}
        </CommentWrapper>)}
    </>
}

const CommentWrapper = styled.div`
    display: flex;
    flex-direction: column;

    &>div {
        display: flex;

        &:first-child {
            color: var(--color-on-surface-light);
            font-size: 1.4rem;
        }

        &> * + * {
            margin-left: 3.5rem;
        }
    }

    && .details {
        display: block;
        margin-left: 0;
        word-break: break-word;

        >* {
            margin-left: 0;
        }

        p {
            margin-bottom: 1rem;
        }
    }

    &> * + * {
        margin-top: 1rem;
    }

    .private-indicator {
        align-items: baseline;
        margin-left: 0;

        i {
            font-size: 1.4rem;
        }

        > * + * {
            margin-left: .5rem;
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 2rem 1.5rem;
    background-color: var(--color-surface);

    ${forMinWidth.sm`
        padding: 2rem 4rem;
    `}

    ${CommentWrapper} + ${CommentWrapper} {
        margin-top: 2rem;
        padding-top: 2rem;
    }

    ${CommentWrapper} ${CommentWrapper}
    {
        margin-left: 3rem;

        ${forMinWidth.sm`
            margin-left: 4rem;
        `}

        ${forMinWidth.md`
            margin-left: 5rem;
        `}
    }
`;
